import { z } from "zod"

import {
  accordionSchema,
  alertSchema,
  areaChartSchema,
  avatarSchema,
  badgeSchema,
  barChartSchema,
  checkboxSchema,
  gridSchema,
  headingSchema,
  kpiCardSchema,
  lineChartSchema,
  pieChartSchema,
  progressBarSchema,
  repeatSchema,
  rowSchema,
  selectSchema,
  separatorSchema,
  tableSchema,
  tabsSchema,
  textInputSchema,
  textSchema,
  tooltipSchema,
} from "@/features/dash/dashboard/catalog"
import { getComponentMetadata } from "@/features/dash/dashboard/component-metadata"

/**
 * Registry of all component schemas with their category labels.
 * To add a new component, add its schema here and it will automatically
 * appear in the generated AI prompt.
 */
export const componentSchemas: { schema: z.ZodTypeAny; category: string }[] = [
  // Chart Components
  { schema: barChartSchema, category: "Chart" },
  { schema: lineChartSchema, category: "Chart" },
  { schema: areaChartSchema, category: "Chart" },
  { schema: pieChartSchema, category: "Chart" },
  // Data Components
  { schema: kpiCardSchema, category: "Data" },
  { schema: tableSchema, category: "Data" },
  { schema: textSchema, category: "Data" },
  { schema: separatorSchema, category: "Data" },
  { schema: alertSchema, category: "Data" },
  { schema: progressBarSchema, category: "Data" },
  // Layout Components
  { schema: gridSchema, category: "Layout" },
  { schema: rowSchema, category: "Layout" },
  // Interactive Layout Components
  { schema: tabsSchema, category: "Interactive Layout" },
  { schema: accordionSchema, category: "Interactive Layout" },
  // Interactive Input Components
  { schema: selectSchema, category: "Interactive Input" },
  { schema: checkboxSchema, category: "Interactive Input" },
  { schema: textInputSchema, category: "Interactive Input" },
  // Data-driven layout
  { schema: repeatSchema, category: "Data-Driven Layout" },
  // shadcn primitives
  { schema: headingSchema, category: "Data" },
  { schema: badgeSchema, category: "Data" },
  { schema: avatarSchema, category: "Data" },
  { schema: tooltipSchema, category: "Layout" },
]

// ─── Zod Introspection Helpers ──────────────────────────────────────

function getShape(schema: z.ZodTypeAny): Record<string, z.ZodTypeAny> | null {
  if (schema instanceof z.ZodObject) {
    return schema.shape as Record<string, z.ZodTypeAny>
  }
  return null
}

/**
 * Describes a Zod type as a human-readable string for documentation.
 */
function describeZodType(schema: z.ZodTypeAny): string {
  if (schema instanceof z.ZodString) return "string"
  if (schema instanceof z.ZodNumber) return "number"
  if (schema instanceof z.ZodBoolean) return "boolean"

  if (schema instanceof z.ZodLiteral) {
    const def = schema._def as unknown as Record<string, unknown>
    const values = def.values as unknown[] | undefined
    const val = values?.[0] ?? (schema as unknown as { value: unknown }).value
    return typeof val === "string" ? `"${val}"` : String(val)
  }

  if (schema instanceof z.ZodEnum) {
    // Zod stores enum values in .options (array) or ._def.entries (object)
    const options = (schema as unknown as { options?: string[] }).options
    if (options) return options.map((v: string) => `"${v}"`).join(" | ")
    const def = schema._def as unknown as Record<string, unknown>
    const entries = def.entries as Record<string, string> | undefined
    if (entries)
      return Object.values(entries)
        .map((v: string) => `"${v}"`)
        .join(" | ")
    return "string"
  }

  if (schema instanceof z.ZodOptional || schema instanceof z.ZodDefault) {
    const def = schema._def as unknown as Record<string, unknown>
    const inner = def.innerType as z.ZodTypeAny | undefined
    return inner ? describeZodType(inner) : "unknown"
  }

  if (schema instanceof z.ZodNullable) {
    const def = schema._def as unknown as Record<string, unknown>
    const inner = def.innerType as z.ZodTypeAny | undefined
    return inner ? `${describeZodType(inner)} | null` : "unknown | null"
  }

  if (schema instanceof z.ZodArray) {
    const def = schema._def as unknown as Record<string, unknown>
    const itemType = def.type as z.ZodTypeAny | undefined
    return itemType ? `${describeZodType(itemType)}[]` : "unknown[]"
  }

  if (schema instanceof z.ZodUnion) {
    const def = schema._def as unknown as Record<string, unknown>
    const options = (def.options ?? []) as z.ZodTypeAny[]
    return options.map(describeZodType).join(" | ")
  }

  if (schema instanceof z.ZodRecord) {
    const def = schema._def as unknown as Record<string, unknown>
    const kt = def.keyType ? describeZodType(def.keyType as z.ZodTypeAny) : "string"
    const vt = def.valueType ? describeZodType(def.valueType as z.ZodTypeAny) : "unknown"
    return `Record<${kt}, ${vt}>`
  }

  if (schema instanceof z.ZodObject) {
    const shape = getShape(schema)
    if (!shape) return "object"
    const fields = Object.entries(shape).map(([key, val]) => {
      const isOpt = val instanceof z.ZodOptional
      return `${key}${isOpt ? "?" : ""}: ${describeZodType(val)}`
    })
    return `{ ${fields.join(", ")} }`
  }

  if (schema instanceof z.ZodLazy) return "(recursive)"
  if (schema instanceof z.ZodNull) return "null"

  return "unknown"
}

/**
 * Extracts the literal type value from a component schema's `type` field.
 */
function extractTypeLiteral(schema: z.ZodTypeAny): string | null {
  const shape = getShape(schema)
  if (!shape) return null

  const typeField = shape.type

  if (typeField instanceof z.ZodLiteral) {
    // Zod stores literal value(s) — handle both .value and ._def.values
    const def = typeField._def as { values?: unknown[] }
    return (def.values?.[0] ?? (typeField as unknown as { value: unknown }).value) as string
  }
  return null
}

/**
 * Generates a documentation block for a single component schema.
 */
function describeComponent(schema: z.ZodTypeAny, category: string, index: number): string {
  const typeName = extractTypeLiteral(schema)
  if (!typeName) return ""

  const shape = getShape(schema)
  if (!shape) return ""

  const props = Object.entries(shape)
    .map(([key, val]) => {
      const isOpt = val instanceof z.ZodOptional
      const typeStr = describeZodType(val)
      const suffix = isOpt ? " (optional)" : ""
      return `  - \`${key}\`: ${typeStr}${suffix}`
    })
    .join("\n")

  // Pull description + concrete example from the metadata registry.
  // First-shot generation quality is markedly better when the AI sees
  // a working example for non-trivial elements (Tabs, Grid, Accordion).
  const meta = getComponentMetadata(typeName)
  const descriptionLine = meta?.description ? `\n  ${meta.description}` : ""
  const exampleBlock = meta?.example ? `\n  Example: \`${JSON.stringify(meta.example)}\`` : ""

  return `${index}. **${typeName}** [${category}]${descriptionLine}\n${props}${exampleBlock}`
}

/**
 * Auto-generates a complete AI system prompt describing all dashboard
 * components by introspecting their Zod schemas.
 *
 * Adding a new component only requires adding it to the `componentSchemas`
 * array -- the prompt will automatically include its documentation.
 */
export function generateDashPrompt(): string {
  const preamble = `Available dashboard components:

The root dashboard spec format:
\`\`\`
{
  title?: string,
  children: Element[],
  state?: Record<string, unknown>,
  sourcePrompt?: string,
  dashboardSessionId?: string,
  queryBindings?: QueryBinding[],
  sourceContext?: { searchServices?, sourceUrls?, trustedMode?, documentIds?, citations? }
}
\`\`\`

Components:`

  const componentDocs = componentSchemas
    .map((entry, idx) => describeComponent(entry.schema, entry.category, idx + 1))
    .filter(Boolean)
    .join("\n\n")

  const footer = `\nWrap your dashboard JSON in a \`\`\`json-render code block. The root object must have: { title?: string, children: Element[] }`

  return `${preamble}\n\n${componentDocs}\n${footer}`
}
